/*
 * Wishlist drawer — Swish-powered side panel.
 *
 * Mirrors CartDrawer's open/close/focus-trap mechanics (cart-drawer.js) but is
 * intentionally independent: its own tag, IDs, and CSS, and NO Shopify
 * section-render API. Swish's <wishlist-page> web component owns the item list,
 * per-item add-to-cart, and remove controls; this element only owns the panel
 * chrome (open/close, overlay, Escape, focus trap, body scroll-lock).
 *
 * <wishlist-page> is lazy-mounted on first open, so pages where the drawer is
 * never opened pay no wishlist-API cost.
 *
 * Coexistence with the cart drawer, without editing cart-drawer.js: opening this
 * drawer closes the cart drawer and the mobile menu drawer; clicking the cart icon
 * soft-closes this drawer. Only one panel is ever active, so the shared body
 * scroll-lock and focus trap cannot collide.
 *
 * Depends on global helpers trapFocus / removeTrapFocus (global.js).
 */
class WishlistDrawer extends HTMLElement {
  constructor() {
    super();

    this.mounted = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#WishlistDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderWishlistIconAccessibility();
  }

  setHeaderWishlistIconAccessibility() {
    const wishlistLink = document.querySelector('#wishlist-icon-bubble');
    if (wishlistLink) {
      wishlistLink.setAttribute('role', 'button');
      wishlistLink.setAttribute('aria-haspopup', 'dialog');
      wishlistLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(wishlistLink);
      });
      wishlistLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(wishlistLink);
        }
      });
    }

    // Mobile menu-drawer row (and any other opt-in trigger) open the same drawer.
    document.querySelectorAll('[data-wishlist-drawer-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      });
    });

    // The cart drawer takes over the same panel region — soft-close ourselves so we
    // don't release the body scroll-lock the cart drawer is about to rely on.
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.addEventListener('click', () => this.softClose());
  }

  mountWishlist() {
    if (this.mounted) return;
    const mount = this.querySelector('[data-wishlist-mount]');
    if (!mount) return;

    const wishlistPage = document.createElement('wishlist-page');
    wishlistPage.setAttribute('show-product-title', '');
    wishlistPage.setAttribute('show-price', '');
    wishlistPage.setAttribute('cta-button', 'add-to-cart');
    mount.appendChild(wishlistPage);
    this.mounted = true;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.closeOtherDrawers();
    this.mountWishlist();

    // A timeout helps the transition trigger reliably (mirrors CartDrawer).
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('WishlistDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    if (typeof HelpCrunch === 'function') HelpCrunch('hideChatWidget');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (typeof HelpCrunch === 'function') HelpCrunch('showChatWidget');
  }

  // Close without releasing the body scroll-lock — used when another drawer is
  // taking over the panel region and will manage the lock itself.
  softClose() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
  }

  closeOtherDrawers() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active') && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }
    document.querySelectorAll('header-drawer details[open]').forEach((details) => details.removeAttribute('open'));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);
